import { ReactElement } from 'react';
import dynamic from 'next/dynamic';
import { HomepageSlice } from '@adapters/prismic/types/homepage';
import {
  FormEmbedSlice,
  HomepageValuePropSlice,
  PageType,
} from '@adapters/prismic/types/slices';
import BlogArticlesGrid from '@components/cms/BlogArticlesGrid';
import HeroImage from '@components/cms/Slices/HeroImage';
import Container from '@components/global/Container';
import { useTranslation } from 'next-i18next';
import {
  BrandedStoreSlice,
  BrandedStoreSliceType,
} from '@adapters/prismic/types/brandedStorePage';
import SecondaryPromoBoxes from '@components/cms/Slices/SecondaryPromoBoxes';
import BrandValues from '../BrandedStoreSlices/BrandValues';
import PrimaryPromoBoxes from '@components/cms/Slices/PrimaryPromoBoxes';
import CategoryNavCarousel from '../Slices/CategoryNavCarousel';
import HeroBanner from '../Slices/HeroBanner';
import { Card } from '@components/plp/FindifyCollectionsLoadingTakeover';
import loadingStyles from '@components/plp/FindifyCollectionsLoadingTakeover/loadingStyles.module.scss';
import { AlgonomyPlacement } from '@components/global/Recommendations';
import { FEATURE_FLAG_NEW_PRODUCT_STRIP } from '@config/feature-flag';

const LinkCardGrid = dynamic(
  () => import('@components/cms/Slices/LinkCardGrid')
);
const IconGrid = dynamic(() => import('@components/cms/Slices/IconGrid'));
const ImageBanner = dynamic(() => import('@components/cms/Slices/ImageBanner'));
const CategoryNavigation = dynamic(
  () => import('@components/cms/Slices/CategoryNavigation')
);
const LinkTiles = dynamic(() => import('@components/cms/Slices/LinkTiles'));
const FormEmbed = dynamic(() => import('@components/cms/Slices/FormEmbed'));
const Recommendations = dynamic(
  () => import('@components/global/Recommendations')
);
const ProductStrip = dynamic(() => import('_components/ProductStrip'));

import styles from './HomepageSliceHandler.module.scss';
import HomepageValueProps from '@components/cms/BrandedStoreSlices/HomepageValueProps/HomepageValueProps';

// Component to handle each different prismic slice type we have defined.
const HomepageSliceHandler = ({
  slice,
  pageType,
  sliceIndex,
  secondSliceZone,
  recommendations,
}: {
  slice?:
    | HomepageSlice
    | BrandedStoreSlice
    | FormEmbedSlice
    | HomepageValuePropSlice;
  pageType: PageType;
  sliceIndex: number;
  secondSliceZone?: boolean;
  recommendations?: AlgonomyPlacement[];
}): ReactElement | null => {
  const { t } = useTranslation('common');

  const recommendationsHome1 = recommendations?.filter(
    (recommendation) => recommendation.name === 'home_page.home1_rr'
  );

  const recommendationsHome3 = recommendations?.filter(
    (recommendation) => recommendation.name === 'home_page.home3_rr'
  );

  switch (slice?.slice_type) {
    case 'hero_image':
      return (
        <HeroImage
          image={slice?.primary?.image}
          link={slice?.primary?.link}
          heading={slice?.primary?.heading}
          content={slice?.primary?.content}
          button_text={slice?.primary?.button_text}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'hero_banner':
      return (
        <HeroBanner
          mediaType={slice.primary?.media_type}
          mobileImage={slice.primary?.mobile_image}
          desktopImage={slice.primary?.desktop_image}
          mobileVideo={slice.primary?.mobile_video}
          desktopVideo={slice.primary?.desktop_video}
          videoLabel={slice.primary?.video_alt_text}
          url={slice.primary?.url}
          blackText={slice.primary?.black_text}
          redText={slice.primary?.red_text}
          redTextPlacement={slice.primary?.red_text_placement}
          redTextNewLine={slice.primary?.red_text_new_line}
          bodyCopy={slice.primary?.body_copy}
          ctaText={slice.primary?.cta_text}
          ctaType={slice.primary?.cta_type}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'primary_promo_boxes':
      return (
        <PrimaryPromoBoxes
          title={slice.primary?.title}
          centerTitle={slice.primary?.center_align}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'secondary_promo_boxes':
      return (
        <SecondaryPromoBoxes
          title={slice.primary.title}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'recommendations_home1':
      return FEATURE_FLAG_NEW_PRODUCT_STRIP ? (
        <>
          {recommendationsHome1?.map((recommendation: any) => (
            <ProductStrip
              placement={recommendation}
              key={recommendation.name}
            />
          ))}
        </>
      ) : (
        <Container className={styles.singleRecommendation}>
          {recommendationsHome1 ? (
            recommendationsHome1?.map((recommendation: any) => (
              <Recommendations
                isHome
                placement={recommendation}
                key={recommendation.name}
              />
            ))
          ) : (
            <div className={loadingStyles.rightCol}>
              {[...Array(8)].map((e, i) => (
                <Card key={i} />
              ))}
            </div>
          )}
        </Container>
      );
    case 'recommendations_home3':
      return FEATURE_FLAG_NEW_PRODUCT_STRIP ? (
        <>
          {recommendationsHome3?.map((recommendation: any) => (
            <ProductStrip
              placement={recommendation}
              key={recommendation.name}
            />
          ))}
        </>
      ) : (
        <Container className={styles.singleRecommendation}>
          {recommendationsHome3 ? (
            recommendationsHome3?.map((recommendation: any) => (
              <Recommendations
                isHome
                placement={recommendation}
                key={recommendation.name}
              />
            ))
          ) : (
            <div className={loadingStyles.rightCol}>
              {[...Array(8)].map((e, i) => (
                <Card key={i} />
              ))}
            </div>
          )}
        </Container>
      );
    case 'category_nav_carousel':
      return (
        <CategoryNavCarousel
          title={slice.primary.title}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'link_card_grid':
      return (
        <LinkCardGrid
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'featured_brands':
      return (
        <IconGrid
          title={slice.primary.section_title}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'image_banner':
      return (
        <ImageBanner
          image={slice.primary.image}
          title={slice.primary.title}
          content={slice.primary.content}
          text_alignment={slice.primary.text_alignment}
          button_text={slice.primary.button_text}
          button_link={slice.primary.button_link}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'category_navigation':
      return (
        <CategoryNavigation
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'link_tiles':
      return (
        <LinkTiles
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={secondSliceZone ? sliceIndex + 2 : sliceIndex}
        />
      );
    case 'featured_blog_posts':
      return (
        <Container>
          <BlogArticlesGrid
            blogPosts={slice.blog_posts}
            title={slice?.primary?.heading}
            linkTitle={t('recent.articles.button')}
          />
        </Container>
      );
    case 'formembed':
      return <FormEmbed html={slice.primary.embedcode} />;
    case BrandedStoreSliceType.brandValues:
      return <BrandValues primary={slice?.primary} items={slice?.items} />;
    case BrandedStoreSliceType.valueProps:
      return (
        <HomepageValueProps
          primary={slice?.primary}
          items={slice?.items}
          reduceMarginTop
        />
      );
    default:
      return null;
  }
};

export default HomepageSliceHandler;
